import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, arrayUnion, collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { ArrowUpTrayIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/20/solid';

const brokerLabel = (broker) => {
  const name = [broker.firstName, broker.lastName].filter(Boolean).join(' ')
    || broker.name || broker.brokerAgeName || broker.brokerageName || broker.companyName || 'Unknown broker';
  return `${name}${broker.email ? ` · ${broker.email}` : ''}`;
};

const DOCUMENT_TYPES = [
  ['Personal', ['Personal Tax Return 2025', 'Personal Tax Return 2024', 'Personal Tax Return 2023', 'Personal Financial Statement', 'Resume']],
  ['Target Business', ['Business Tax Return 2025', 'Business Tax Return 2024', 'Business Tax Return 2023', 'Interim P&L 2026 YTD', 'Trailing 12 Months P&L', 'Balance Sheet', 'CIM', 'Debt Schedule']],
  ['Transaction', ['LOI', 'Business Valuation', 'Lease Agreement', 'Real Estate Valuation', 'Franchise Agreement']],
  ['Corporate / Other', ['Articles of Organization', 'Operating Agreement', 'AR Aging', 'AP Aging', 'Bank Statements', 'Other']],
];

export default function UploadBrokerPrequalificationModal({ open, onClose, onUploaded }) {
  const [brokers, setBrokers] = useState([]);
  const [brokerId, setBrokerId] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [amount, setAmount] = useState('');
  const [prequalFile, setPrequalFile] = useState(null);
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    getDocs(collection(db, 'brokers')).then((snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => brokerLabel(a).localeCompare(brokerLabel(b)));
      setBrokers(rows);
    }).catch(() => setError('Could not load brokers.'));
  }, [open]);

  const selectedBroker = useMemo(() => brokers.find(b => b.id === brokerId), [brokers, brokerId]);
  if (!open) return null;

  const resetAndClose = (force = false) => {
    if (loading && !force) return;
    setBrokerId(''); setBusinessName(''); setAmount(''); setPrequalFile(null); setSupportingFiles([]); setError('');
    onClose();
  };

  const upload = async (event) => {
    event.preventDefault();
    if (!brokerId || !prequalFile) {
      setError('Choose a broker and attach the prequalification PDF.');
      return;
    }
    if (prequalFile.type !== 'application/pdf' && !prequalFile.name.toLowerCase().endsWith('.pdf')) {
      setError('The main prequalification must be a PDF.');
      return;
    }
    if (supportingFiles.some(item => !item.documentType || (item.documentType === 'Other' && !item.customLabel.trim()))) {
      setError('Choose a document type for every additional file.');
      return;
    }
    setLoading(true); setError('');
    try {
      const now = new Date();
      const parsedAmount = Number(String(amount).replace(/[^0-9.]/g, '')) || null;
      const displayName = businessName.trim() || `Uploaded prequalification · ${now.toLocaleDateString()}`;
      const appRef = await addDoc(collection(db, 'prequalificationApplications'), {
        businessName: displayName,
        brokerId,
        brokerName: selectedBroker ? brokerLabel(selectedBroker).split(' · ')[0] : '',
        brokerEmail: selectedBroker?.email || '',
        status: 'prequalified',
        prequalified: true,
        prequalifiedAmount: parsedAmount,
        projectedSalePrice: parsedAmount,
        source: 'admin_upload',
        createdBy: 'admin',
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        prequalifiedAt: serverTimestamp(),
        summaryUploaded: true,
      });

      const files = [{ file: prequalFile, label: 'Prequalification Summary', isSummary: true }, ...supportingFiles.map(item => ({ file: item.file, label: item.documentType === 'Other' ? item.customLabel.trim() : item.documentType, isSummary: false }))];
      for (const item of files) {
        const safeName = item.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `prequalifications/admin/${appRef.id}/${item.isSummary ? 'summary' : 'supporting'}/${Date.now()}-${safeName}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, item.file);
        const fileUrl = await getDownloadURL(fileRef);
        await addDoc(collection(db, 'prequalificationMetadata'), {
          applicationId: appRef.id,
          brokerId,
          documentName: item.label,
          label: item.label,
          fileName: item.file.name,
          fileUrl,
          storagePath: path,
          contentType: item.file.type || '',
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'admin',
          status: 'uploaded',
          isPrequalificationSummary: item.isSummary,
        });
        if (item.isSummary) {
          await setDoc(appRef, { prequalificationSummaryUrl: fileUrl, prequalificationSummaryFileName: item.file.name }, { merge: true });
        }
      }

      await setDoc(doc(db, 'brokers', brokerId), {
        prequalificationApplications: arrayUnion(appRef.id),
        lastUpdated: serverTimestamp(),
      }, { merge: true });
      onUploaded?.({ id: appRef.id, businessName: displayName });
      resetAndClose(true);
    } catch (err) {
      console.error('Prequalification upload failed:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true" aria-label="Upload broker prequalification">
      <form onSubmit={upload} className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div><h2 className="text-lg font-semibold text-gray-950">Upload prequalification</h2><p className="mt-1 text-sm text-gray-500">Assign the completed prequal and any supporting files to a broker portal.</p></div>
          <button type="button" onClick={() => resetAndClose()} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="space-y-5 px-6 py-6">
          <label className="block"><span className="text-sm font-medium text-gray-800">Broker <span className="text-red-500">*</span></span><select value={brokerId} onChange={e => setBrokerId(e.target.value)} className="mt-2 block w-full rounded-lg border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"><option value="">Choose a broker…</option>{brokers.map(b => <option key={b.id} value={b.id}>{brokerLabel(b)}</option>)}</select></label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block"><span className="text-sm font-medium text-gray-800">Business name <span className="font-normal text-gray-400">optional</span></span><input value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. El Dorado Pools" className="mt-2 block w-full rounded-lg border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500" /></label>
            <label className="block"><span className="text-sm font-medium text-gray-800">Prequalified amount <span className="font-normal text-gray-400">optional</span></span><input value={amount} onChange={e => setAmount(e.target.value)} placeholder="$850,000" className="mt-2 block w-full rounded-lg border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500" /></label>
          </div>
          <label className="block rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 p-5 text-center hover:border-blue-400"><DocumentTextIcon className="mx-auto h-8 w-8 text-blue-600" /><span className="mt-2 block text-sm font-semibold text-gray-900">{prequalFile ? prequalFile.name : 'Choose prequalification PDF'}</span><span className="mt-1 block text-xs text-gray-500">Required · this is the primary file shown to the broker</span><input type="file" accept="application/pdf,.pdf" className="sr-only" onChange={e => setPrequalFile(e.target.files?.[0] || null)} /></label>
          <label className="block"><span className="text-sm font-medium text-gray-800">Other files <span className="font-normal text-gray-400">optional</span></span><input type="file" multiple onChange={e => setSupportingFiles(Array.from(e.target.files || []).map((file, index) => ({ id: `${file.name}-${file.size}-${index}`, file, documentType: '', customLabel: '' })))} className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:rounded-lg file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:font-medium file:text-gray-700 hover:file:bg-gray-200" /></label>
          {supportingFiles.length > 0 && <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Label each additional file</p>{supportingFiles.map((item, index) => <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-3"><div className="flex items-center justify-between gap-3"><p className="min-w-0 truncate text-sm font-medium text-gray-800">{item.file.name}</p><button type="button" onClick={() => setSupportingFiles(files => files.filter((_, i) => i !== index))} className="text-xs font-medium text-red-600 hover:text-red-700">Remove</button></div><select value={item.documentType} onChange={e => setSupportingFiles(files => files.map((row, i) => i === index ? { ...row, documentType: e.target.value } : row))} className="mt-2 block w-full rounded-lg border-gray-300 text-sm"><option value="">What type of file is this?</option>{DOCUMENT_TYPES.map(([category, types]) => <optgroup key={category} label={category}>{types.map(type => <option key={type} value={type}>{type}</option>)}</optgroup>)}</select>{item.documentType === 'Other' && <input value={item.customLabel} onChange={e => setSupportingFiles(files => files.map((row, i) => i === index ? { ...row, customLabel: e.target.value } : row))} placeholder="Name this document" className="mt-2 block w-full rounded-lg border-gray-300 text-sm" />}</div>)}</div>}
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4"><button type="button" onClick={() => resetAndClose()} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">Cancel</button><button disabled={loading} className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"><ArrowUpTrayIcon className="mr-2 h-4 w-4" />{loading ? 'Uploading…' : 'Upload to broker'}</button></div>
      </form>
    </div>
  );
}
